import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Collect } from '../../../models/collect';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import { useTheme, useMediaQuery, Tooltip, Typography, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent } from '@mui/material';
import { Office } from '../../../models/office';
import { useSelection } from '../../../components/context/SelectionContext';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { parseISO, format } from 'date-fns';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const Cobros: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Administrar Cobros";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [collects, setCollection] = useState<Collect[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
    const { drawerOpen } = useDrawer();
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/cobros');

    // Cargar oficinas
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                setCollection([])
                const officesResponse = await axios.get('alloffices');
                setOffices(officesResponse.data.data); // Asegúrate de que la respuesta tenga la estructura correcta
            } catch (error) {
                showAlert('Error recuperando las oficinas:', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                setCollection([]); // Limpiar los datos anteriores antes de hacer la nueva consulta
                const { data } = await axios.get(`collectsoffice/${office_id}`);
                setCollection(data);
                await new Promise((resolve) => setTimeout(resolve, 1000)); // Simular un retraso
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                console.error('Error fetching data: ', error);
                showAlert('Error fetching data!', 'error');
            } finally {
                hideLoading();
            }
        };

        if (office_id) { // Solo hacer la consulta si office_id tiene un valor válido
            fetchData();
        } else {
            setCollection([]); // Limpiar collects si no hay office_id seleccionado
        }
    }, [office_id]); // Dependencia: office_id
    const handleDeleteCollect = async (id: number) => {
        if (!canDelete) return;

        const confirm = window.confirm(
            "¿Estás seguro que deseas eliminar este cobro?\nSe eliminarán también todos los registros asociados (cajas, usuarios, cierres)."
        );

        if (!confirm) return;

        showLoading();

        try {
            await axios.delete(`/collects/${id}`);

            // Actualiza la lista de collects sin volver a hacer la consulta
            setCollection(prev => prev.filter((collect: Collect) => collect.id !== id));

            showAlert("Cobro eliminado correctamente", "success");
        } catch (error) {
            console.error("Error eliminando el cobro:", error);
            showAlert("Ocurrió un error al eliminar el cobro", "error");
        } finally {
            hideLoading();
        }
    };

    const simpleColumns: SimpleColumn[] = [
        {
            field: 'office_name',
            headerName: 'Oficina',
            minWidth: 150,
            flex: 1,
            wrapText: true,
        },
        {
            field: 'collect_name',
            headerName: 'Cobro',
            minWidth: 150,
            flex: 1,
            wrapText: true,
        },
        {
            field: 'time',
            headerName: 'Horario Operación',
            minWidth: 140,
            wrapText: true,
        },
        {
            field: 'ingress_date',
            headerName: 'Ingreso',
            minWidth: 170,
        },
        {
            field: 'responsible',
            headerName: 'Responsable',
            minWidth: 160,
            flex: 1,
            wrapText: true,
        },
        {
            field: 'opt_edit',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para editar"
                    tooltip="Editar"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to={`/administrar/cobros/${row.id}/editar`}
                >
                        <EditIcon fontSize="small" />
                </PermissionButton>
            ),
        },
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => {
                const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
                const handleClick = () => {
                    if (Number.isFinite(numericId)) {
                        handleDeleteCollect(numericId);
                    }
                };
                return (
                    <PermissionButton
                        allowed={canDelete}
                        deniedTooltip="Sin permiso para eliminar"
                        tooltip="Eliminar"
                        variant="contained"
                        color="error"
                        sx={simpleTableIconButtonSx}
                        onClick={handleClick}
                    >
                            <DeleteIcon fontSize="small" />
                    </PermissionButton>
                );
            },
        },
    ];

    const rows: SimpleRow[] = collects.map((collect: Collect) => {
        let ingressDate = '-';
        if (collect.ingress_date) {
            try {
                ingressDate = format(parseISO(collect.ingress_date), 'yyyy-MM-dd HH:mm:ss');
            } catch (error) {
                ingressDate = collect.ingress_date;
            }
        }

        const timeRange = collect.time_open && collect.time_close
            ? `${collect.time_open} - ${collect.time_close}`
            : '-';

        return {
            id: collect.id as RowId,
            office_name: collect.office?.name ?? 'Sin oficina',
            collect_name: collect.name,
            time: timeRange,
            ingress_date: ingressDate,
            responsible: collect.user ? `${collect.user.first_name} ${collect.user.last_name}` : 'No asignado',
        };
    });

    return (
        <HeaderResponsive>
            <StickyListToolbar>
                <Title>Información Cobros</Title>
                <Box sx={{ ml: 'auto' }}>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Agregar"
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/cobros/add"
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </PermissionButton>
                </Box>
            </StickyListToolbar>
            <Card>
                <CardContent
                    sx={{
                        width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                    }}
                >
                    <form onSubmit={(e) => e.preventDefault()}>

                        <Grid item xs={12} sm={6} xl={6}>
                            <FormControl fullWidth>
                                <InputLabel id="form-layouts-separator-select-label">
                                    Seleccionar Oficina
                                </InputLabel>
                                {offices && offices.length > 0 ? (
                                    <Select
                                        label="Seleccionar Oficina"
                                        value={office_id || ""} // Usar un valor por defecto
                                        id="form-layouts-separator-select"
                                        labelId="form-layouts-separator-select-label"
                                        size="small"
                                        onChange={(event) => setOffice_id(event.target.value)}
                                        required
                                        disabled={offices.length === 0} // Deshabilitar si no hay oficinas
                                    >
                                        {offices.map((office: Office) => (
                                            <MenuItem key={office.id} value={office.id}>
                                                {office.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                ) : (
                                    <Typography variant="body1" color="textSecondary">
                                        No hay oficinas disponibles.
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </form>

                    <Box sx={{ maxHeight: 800, overflowY: 'auto', my: 2 }}>
                        {collects.length > 0 ? (
                            <SimpleCustomTable
                                rows={rows}
                                columns={simpleColumns}
                                maxHeight={800}
                                rowHeight={24}
                                showBorders
                                showCount
                                exportCsv
                                exportFileName="cobros.csv"
                                wrapCellContent
                                columnGroups={[{ header: 'Opciones', fields: ['opt_edit', 'opt_delete'] }]}
                            />
                        ) : (
                            <EmptyStateMessage message="No existen registros" align="left" />
                        )}
                    </Box>
                </CardContent>
            </Card>
        </HeaderResponsive>
    );
}

export default Cobros;
